import type { CreationArtifactEdgeV1, CreationArtifactUsageV1, CreationArtifactV1 } from '@djgg/creations';
import type { ShowArtifact } from './creationShowModel.js';

function payloadRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function payloadText(artifact: CreationArtifactV1): string {
  const payload = payloadRecord(artifact.payload);
  for (const key of ['summary', 'concept', 'text', 'tagline']) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function selectedCandidateId(selection: CreationArtifactV1 | undefined): string | undefined {
  const payload = payloadRecord(selection?.payload);
  for (const key of ['selectedCandidateArtifactId', 'candidateArtifactId', 'selectedArtifactId']) {
    const value = payload[key];
    if (typeof value === 'string' && value) return value;
  }
  return undefined;
}

function durableMediaKind(artifact: CreationArtifactV1): Exclude<ShowArtifact['kind'], 'inspiration'> {
  // A MIME type describes the bytes more reliably than the artifact's semantic
  // role (a reference-media artifact may be a video, for example). URI-less
  // artifacts remain inspectable text cards instead of broken media players.
  if (!artifact.uri) return 'discovery';
  const contentType = artifact.contentType?.toLowerCase() ?? '';
  if (contentType.startsWith('video/')) return 'video';
  if (contentType.startsWith('audio/')) return 'audio';
  if (contentType.startsWith('image/')) return 'image';
  if (artifact.kind === 'proof') return 'capture';
  if (artifact.kind.startsWith('sound-')) return 'audio';
  return 'image';
}

/** Public durable title/receipt artifacts adapted for the existing film shell. */
export function creationArtifactShowArtifacts(
  artifacts: readonly CreationArtifactV1[],
  selectedTitle: string,
  edges: readonly CreationArtifactEdgeV1[] = [],
  usages: readonly CreationArtifactUsageV1[] = [],
): ShowArtifact[] {
  const visible = artifacts.filter(artifact => artifact.lifecycle !== 'rejected'
    && artifact.quality?.status !== 'rejected' && artifact.safety?.status !== 'rejected');
  const selection = [...visible].reverse().find(artifact => artifact.kind === 'title-selection');
  const selectedId = selectedCandidateId(selection);
  return visible.flatMap<ShowArtifact>(artifact => {
    const candidate = artifact.kind === 'title-candidate';
    const selected = candidate && (artifact.artifactId === selectedId
      || (!selectedId && !!selectedTitle && artifact.title.localeCompare(selectedTitle, undefined, { sensitivity: 'base' }) === 0));
    const kind = durableMediaKind(artifact);
    const category: ShowArtifact['category'] = artifact.kind === 'proof' ? 'proof'
      : artifact.kind.startsWith('sound-') ? 'sound'
        : ['title-candidate', 'title-selection'].includes(artifact.kind) ? 'identity'
          : artifact.roleHints.some(role => /character|cast|enemy|player|sprite/i.test(role)) ? 'characters'
            : artifact.roleHints.some(role => /cover|identity|menu|logo/i.test(role)) ? 'identity'
              : artifact.roleHints.some(role => /world|environment|background|palette/i.test(role)) ? 'world'
                : 'gameplay';
    const parents = edges.filter(edge => edge.childArtifactId === artifact.artifactId)
      .map(edge => ({ parentArtifactId: edge.parentArtifactId, relation: edge.relation }));
    const children = edges.filter(edge => edge.parentArtifactId === artifact.artifactId)
      .map(edge => ({ childArtifactId: edge.childArtifactId, relation: edge.relation }));
    const reference = artifact.eligibility === 'inspiration-only' || artifact.provenance.origin === 'reference';
    const boundedReference = reference && !!artifact.uri
      && typeof artifact.width === 'number' && artifact.width > 0
      && typeof artifact.height === 'number' && artifact.height > 0;
    const common = {
      id: artifact.artifactId,
      title: artifact.title,
      summary: payloadText(artifact),
      category,
      state: selected ? 'selected' as const : artifact.lifecycle === 'candidate' ? 'recommended' as const : 'current' as const,
      label: artifact.eligibility === 'inspiration-only' ? 'Inspiration · external reference'
        : artifact.kind === 'idea-receipt' ? 'Your idea · saved'
        : artifact.kind === 'title-selection' ? 'Title selection · locked'
          : artifact.kind === 'title-candidate' ? (selected ? 'Selected title' : 'Title candidate')
            : artifact.provenance.label ?? artifact.kind.replaceAll('-', ' '),
      openable: artifact.lifecycle === 'accepted',
      durableKind: artifact.kind,
      arrivalSeq: artifact.arrivalSeq,
      arrivedAt: artifact.arrivedAt,
      selected,
      eligibility: artifact.eligibility,
      lifecycle: artifact.lifecycle,
      provenance: artifact.provenance,
      quality: artifact.quality,
      safety: artifact.safety,
      contentType: artifact.contentType,
      lineage: { parents, children },
      usages: usages.filter(usage => usage.artifactId === artifact.artifactId),
    };
    if (boundedReference) return [{ ...common, kind: 'inspiration' as const, url: artifact.uri!, origin: 'reference' as const,
      intrinsicWidth: artifact.width!, intrinsicHeight: artifact.height! } satisfies ShowArtifact];
    return [{ ...common, kind, url: reference ? undefined : artifact.uri,
      origin: reference ? 'reference' as const
        : artifact.provenance.origin === 'creator' ? 'intent' as const : 'current-run' as const,
      unavailable: reference && !!artifact.uri,
    } satisfies ShowArtifact];
  });
}
